import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/datetime/x-date-picker-dropdown.js';
import '@adornis/forms/x-radiogroup.js';
import '@adornis/popover/x-tooltip.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-toggle-button.js';
import '@chancenwerk/design/components/fonts.js';
import { WorkshopAPI } from '@chancenwerk/workshops/api/WorkshopAPI.js';
import { Workshop } from '@chancenwerk/workshops/entities/Workshop.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/workshops/entities/WorkshopAttendanceStatus.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { switchMap } from 'rxjs';

/**
 * @customElement anmeldung-page-registration-workshop-management
 */
@customElement('anmeldung-page-registration-workshop-management')
export class AnmeldungPageRegistrationWorkshopManagement extends ChemistryLitElement {
  size = new WindowSizeController(this);

  @property() workshopID = new RXController(this, '');

  @state() _accessToken = new RXController(this, routingState.value.searchParams.get('accessToken'));
  @state() _workshop = new RXController(
    this,
    this.workshopID.observable.pipe(
      switchMap(id =>
        Workshop.subscribeByID<Workshop>(id ?? '')({
          ...Workshop.allFields,
        }),
      ),
    ),
  );
  @state() _attendance = new RXController(
    this,
    this._accessToken.observable.pipe(
      switchMap(async token => {
        return WorkshopAPI.getAttendanceByAccessToken(token ?? '')({
          date: 1,
          state: 1,
        });
      }),
    ),
  );

  private _actions() {
    const state = this._attendance.value?.state;
    if (state === WorkshopAttendanceStatus.REGISTERED) {
      return html`<cr-button
        tone="error"
        @click=${async () => {
          ChromiumDialog.guard(
            async () => {
              await WorkshopAPI.stornoAttendanceByAccessToken(this._accessToken.value ?? '')();
              location.reload();
            },
            {
              hasToConfirm: true,
              confirmText: 'Willst du die Anmeldung wirklich stornieren?',
            },
          );
        }}
        >Stornieren</cr-button
      >`;
    }

    return html`<cr-text>Keine Aktionen verfügbar...</cr-text>`;
  }

  public override render() {
    if (!this._workshop.value || !this._accessToken.value) {
      return nothing;
    }

    return html` <cr-section>
      <cr-card space="md" padding="lg" ${css({ marginTop: this.spacing.xl })}>
        <cr-h1 ${css({ marginBottom: this.spacing.lg })}>${this._workshop.value?.title}</cr-h1>
        <chancen-component-card-title ${css({ marginTop: this.spacing.xl })}>
          Beschreibung
        </chancen-component-card-title>
        <cr-text>${unsafeHTML(this._workshop.value?.description ?? '')}</cr-text>
        <chancen-component-card-title ${css({ marginTop: this.spacing.xl })}>
          Informationen zum Termin
        </chancen-component-card-title>
        <cr-text>${unsafeHTML(this._workshop.value?.appointmentInfo ?? '')}</cr-text>
        <chancen-component-card-title ${css({ marginTop: this.spacing.xl })}> Wann </chancen-component-card-title>
        <cr-text>${this._attendance.value?.date?.toFormat('dd.MM.yyyy HH:mm')}</cr-text>
        <chancen-component-card-title ${css({ marginTop: this.spacing.xl })}> Status </chancen-component-card-title>
        <cr-text>${this._attendance.value?.state}</cr-text>
        <chancen-component-card-title ${css({ marginTop: this.spacing.xl })}> Aktionen </chancen-component-card-title>
        ${this._actions()}
      </cr-card>
    </cr-section>`;
  }
}
